import { useState } from 'react';
import { Download } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DownloadDatasetButtonProps {
  resourceName: string;
  start: string;
  end: string;
  downloadType: 'productivity' | 'pastresourceusage';
  disabled?: boolean;
}

export default function DownloadDatasetButton({
  resourceName,
  start,
  end,
  downloadType,
  disabled,
}: DownloadDatasetButtonProps) {
  const [busy, setBusy] = useState(false);

  async function onClick() {
    if (busy) return;
    setBusy(true);
    try {
      const url =
        `/v1/download?resource-id=${encodeURIComponent(resourceName)}` +
        `&start=${encodeURIComponent(start)}` +
        `&end=${encodeURIComponent(end)}` +
        `&downloadtype=${downloadType}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Download failed: HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const filename =
        parseFilename(res.headers.get('Content-Disposition')) ||
        defaultFilename(downloadType, resourceName);

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert(
        `Download failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border',
        'hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0',
      )}
      title="Download CSV for the current time range"
      aria-label="Download Dataset"
    >
      <Download className="h-4 w-4" />
      Download Dataset
    </button>
  );
}

function parseFilename(header: string | null): string {
  if (!header) return '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return m?.[1] ?? '';
}

function defaultFilename(
  downloadType: 'productivity' | 'pastresourceusage',
  resourceName: string,
): string {
  const safe = resourceName.replace(/[^A-Za-z0-9._-]/g, '_');
  return downloadType === 'productivity'
    ? `productivitygaps_${safe}.csv`
    : `usage_${safe}.csv`;
}
